import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { lazy, Suspense } from "react";
import { useSentryPerformance } from "./hooks/useSentryPerformance";
// useNavigationRecovery 제거 - 싱글톤 패턴 제거로 더 이상 필요없음
import { Skeleton } from "./components/ui/skeleton";
import { Card, CardContent } from "./components/ui/card";

// Pages - Core app pages (keep direct imports for fast initial load)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import CourseSearch from "./pages/CourseSearch";
import CourseDetail from "./pages/CourseDetail";
import ProductDetail from "./pages/ProductDetail";
import Booking from "./pages/Booking";
import Payment from "./pages/Payment";
import BookingSuccess from "./pages/BookingSuccess";
import BookingDetail from "./pages/BookingDetail";
import Auth from "./pages/Auth";
import BookingHistory from "./pages/BookingHistory";
import AccommodationSearch from "./pages/AccommodationSearch";
import AccommodationDetailPage from "./features/accommodation/pages/AccommodationDetailPage";
import MyPage from "./pages/MyPage";

// Less frequently used pages - Lazy load for optimization
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LocationTerms = lazy(() => import("./pages/LocationTerms"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage"));

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import MobileBottomNav from "./components/MobileBottomNav";

// Admin App - Complete admin chunk separation
const AdminApp = lazy(() => import("./components/AdminApp"));

// Loading components for consistent loading states
const PageLoadingFallback = () => (
  <Card className="m-6">
    <CardContent className="p-6">
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-4 w-full" />
          ))}
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminLoadingFallback = () => (
  <Card className="m-6">
    <CardContent className="p-6">
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        
        {/* Content skeleton */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          
          {/* Table skeleton */}
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Page transition configuration
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -20,
  },
};

const pageTransition = {
  type: "tween" as const,
  ease: "easeInOut" as const,
  duration: 0.3,
};

/**
 * Animated page wrapper component
 */
const AnimatedPage = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
  >
    {children}
  </motion.div>
);

/**
 * Animated routes component with navigation recovery
 */
const AnimatedRoutes = () => {
  const location = useLocation();
  
  // Sentry 성능 모니터링 훅 추가
  useSentryPerformance();
  
  // Navigation recovery hook 제거됨 - 싱글톤 패턴 제거로 불필요
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public Routes */}
        <Route path="/" element={
          <AnimatedPage>
            <Index />
          </AnimatedPage>
        } />
        
        <Route path="/auth" element={
          <AnimatedPage>
            <Auth />
          </AnimatedPage>
        } />
        
        
        <Route path="/courses" element={
          <AnimatedPage>
            <CourseSearch />
          </AnimatedPage>
        } />

        {/* @ROUTE:ACC-002-001 */}
        <Route path="/stay" element={
          <AnimatedPage>
            <AccommodationSearch />
          </AnimatedPage>
        } />

        {/* @ROUTE:ACC-003-001 */}
        <Route path="/stay/:id" element={
          <AnimatedPage>
            <AccommodationDetailPage />
          </AnimatedPage>
        } />

        <Route path="/course-detail/:id" element={
          <AnimatedPage>
            <CourseDetail />
          </AnimatedPage>
        } />
        
        <Route path="/product-detail/:id" element={
          <AnimatedPage>
            <ProductDetail />
          </AnimatedPage>
        } />
        
        {/* Protected Routes */}
        <Route path="/booking/new/:productId" element={
          <ProtectedRoute>
            <AnimatedPage>
              <Booking />
            </AnimatedPage>
          </ProtectedRoute>
        } />
        
        <Route path="/payment/:id" element={
          <ProtectedRoute>
            <AnimatedPage>
              <Payment />
            </AnimatedPage>
          </ProtectedRoute>
        } />
        
        <Route path="/booking-success/:id" element={
          <ProtectedRoute>
            <AnimatedPage>
              <BookingSuccess />
            </AnimatedPage>
          </ProtectedRoute>
        } />
        
        <Route path="/booking-history" element={
          <ProtectedRoute>
            <AnimatedPage>
              <BookingHistory />
            </AnimatedPage>
          </ProtectedRoute>
        } />

        <Route path="/my" element={
          <ProtectedRoute>
            <AnimatedPage>
              <MyPage />
            </AnimatedPage>
          </ProtectedRoute>
        } />

        <Route path="/booking/detail/:bookingId" element={
          <ProtectedRoute>
            <AnimatedPage>
              <BookingDetail />
            </AnimatedPage>
          </ProtectedRoute>
        } />
        
        <Route path="/terms" element={
          <AnimatedPage>
            <Suspense fallback={<PageLoadingFallback />}>
              <TermsOfService />
            </Suspense>
          </AnimatedPage>
        } />
        
        <Route path="/privacy" element={
          <AnimatedPage>
            <Suspense fallback={<PageLoadingFallback />}>
              <PrivacyPolicy />
            </Suspense>
          </AnimatedPage>
        } />
        
        <Route path="/location-terms" element={
          <AnimatedPage>
            <Suspense fallback={<PageLoadingFallback />}>
              <LocationTerms />
            </Suspense>
          </AnimatedPage>
        } />
        
        <Route path="/refund-policy" element={
          <AnimatedPage>
            <Suspense fallback={<PageLoadingFallback />}>
              <RefundPolicyPage />
            </Suspense>
          </AnimatedPage>
        } />
        
        <Route path="/faq" element={
          <AnimatedPage>
            <Suspense fallback={<PageLoadingFallback />}>
              <FAQPage />
            </Suspense>
          </AnimatedPage>
        } />
        
        {/* Admin Routes - Complete chunk separation */}
        <Route path="/admin/*" element={
          <AnimatedPage>
            <Suspense fallback={<AdminLoadingFallback />}>
              <AdminApp />
            </Suspense>
          </AnimatedPage>
        } />
        
        {/* 404 Route */}
        <Route path="*" element={
          <AnimatedPage>
            <NotFound />
          </AnimatedPage>
        } />
      </Routes>
    </AnimatePresence>
  );
};

/**
 * Main App component
 * - Pure routing component
 * - All providers handled by AppProviders
 * - Simplified and focused on routing logic
 */
const App = () => {
  const location = useLocation();

  // 하단 GNB를 표시할 페이지 경로 (메인 페이지들만)
  const bottomNavPages = ['/', '/courses', '/my', '/stay'];
  const showBottomNav = bottomNavPages.includes(location.pathname);

  return (
    <>
      <AnimatedRoutes />
      {showBottomNav && <MobileBottomNav />}
    </>
  );
};

export default App;